export interface Review {
  id: string;
  author: string;
  avatar: string;
  rating: number;
  comment: string;
  date: string;
  verified: boolean;
  title: string;
}

export interface Product {
  id: string;
  name: string;
  category: string;
  price: number;
  originalPrice: number;
  images: string[];
  description: string;
  shortDesc: string;
  specs: Record<string, string>;
  rating: number;
  reviewCount: number;
  reviews: Review[];
  badge?: string;
  inStock: boolean;
  sku: string;
}

export const categories = [
  { id: "power-tools", name: "Power Tools", icon: "ri-flashlight-line" },
  { id: "hand-tools", name: "Hand Tools", icon: "ri-tools-line" },
  { id: "measuring", name: "Measuring Tools", icon: "ri-ruler-line" },
  { id: "fasteners", name: "Fasteners & Hardware", icon: "ri-settings-3-line" },
  { id: "safety", name: "Safety Equipment", icon: "ri-shield-check-line" },
  { id: "storage", name: "Storage Solutions", icon: "ri-archive-line" },
];

export const products: Product[] = [
  {
    id: "p001",
    name: "DeWalt 20V MAX Cordless Drill Driver Kit",
    category: "power-tools",
    price: 189,
    originalPrice: 229,
    images: [
      "https://public.readdy.ai/ai/img_res/79f6759cbd774e0135b7fd9c836f129f.jpg",
      "https://readdy.ai/api/search-image?query=cordless%20drill%20driver%20side%20view%20professional%20tool%20kit%20with%20battery%20pack%2C%20clean%20white%20background%20product%20shot&width=800&height=800&seq=p001b&orientation=squarish",
      "https://readdy.ai/api/search-image?query=drill%20driver%20chuck%20close%20up%20detail%20professional%20hardware%20tool%2C%20white%20background&width=800&height=800&seq=p001c&orientation=squarish",
      "https://readdy.ai/api/search-image?query=power%20drill%20in%20use%20on%20wooden%20surface%20construction%20work%2C%20professional%20contractor%20tool&width=800&height=800&seq=p001d&orientation=squarish",
      "https://readdy.ai/api/search-image?query=drill%20driver%20battery%20charger%20kit%20accessories%20professional%20hardware%2C%20white%20background&width=800&height=800&seq=p001e&orientation=squarish",
      "https://readdy.ai/api/search-image?query=cordless%20drill%20driver%20carrying%20case%20storage%20professional%20tool%20set%2C%20clean%20background&width=800&height=800&seq=p001f&orientation=squarish",
    ],
    description: "The DeWalt 20V MAX Cordless Drill Driver Kit delivers up to 300 unit watts out (UWO) of power for a wide range of drilling and fastening applications. Features a high-speed transmission with 2 speeds (0-450 / 1,500 RPM) for versatility. The compact, lightweight design fits into tight areas. Includes 2 batteries, charger, and carrying bag.",
    shortDesc: "Professional 20V cordless drill with 2-speed transmission, 2 batteries included.",
    specs: {
      "Voltage": "20V MAX",
      "Chuck Size": "1/2 inch",
      "Speed": "0-450 / 0-1,500 RPM",
      "Torque": "300 UWO",
      "Weight": "3.6 lbs",
      "Battery": "2x 2.0Ah Li-Ion",
      "Warranty": "3 Years",
    },
    rating: 4.8,
    reviewCount: 247,
    badge: "Best Seller",
    inStock: true,
    sku: "SL-DRL-001",
    reviews: [
      { id: "r001a", author: "Mike Thompson", avatar: "MT", rating: 5, title: "Excellent drill for the price!", comment: "I've been using this drill for 6 months on various construction projects. The battery life is impressive and the torque is more than enough for most tasks. Highly recommend!", date: "2024-11-15", verified: true },
      { id: "r001b", author: "Sarah Johnson", avatar: "SJ", rating: 5, title: "Perfect for home projects", comment: "Bought this for home renovation. It's lightweight, powerful, and the two batteries mean I never have to stop working. Great purchase!", date: "2024-10-28", verified: true },
      { id: "r001c", author: "Carlos Rivera", avatar: "CR", rating: 4, title: "Solid professional tool", comment: "Good drill overall. The 2-speed setting is very useful. Only minor complaint is the charger could be faster, but the performance is excellent.", date: "2024-09-12", verified: true },
    ],
  },
  {
    id: "p002",
    name: "Milwaukee M18 FUEL Circular Saw",
    category: "power-tools",
    price: 349,
    originalPrice: 419,
    images: [
      "https://readdy.ai/api/search-image?query=Milwaukee%20circular%20saw%20professional%20power%20tool%20red%20color%20on%20white%20background%2C%20high%20quality%20product%20photography&width=800&height=800&seq=p002a&orientation=squarish",
      "https://readdy.ai/api/search-image?query=circular%20saw%20blade%20close%20up%20detail%20professional%20cutting%20tool%2C%20white%20background%20product%20shot&width=800&height=800&seq=p002b&orientation=squarish",
      "https://readdy.ai/api/search-image?query=circular%20saw%20cutting%20wood%20plank%20construction%20site%20professional%20contractor&width=800&height=800&seq=p002c&orientation=squarish",
      "https://readdy.ai/api/search-image?query=circular%20saw%20side%20profile%20view%20professional%20power%20tool%2C%20clean%20white%20background&width=800&height=800&seq=p002d&orientation=squarish",
      "https://readdy.ai/api/search-image?query=circular%20saw%20with%20battery%20pack%20professional%20cordless%20tool%20kit&width=800&height=800&seq=p002e&orientation=squarish",
      "https://readdy.ai/api/search-image?query=circular%20saw%20guard%20safety%20feature%20close%20up%20professional%20hardware%20tool&width=800&height=800&seq=p002f&orientation=squarish",
    ],
    description: "The Milwaukee M18 FUEL Circular Saw delivers the power, speed, and run-time to replace corded saws. Features POWERSTATE Brushless Motor, REDLINK PLUS Intelligence, and REDLITHIUM Battery Technology. Cuts 2x material at 45° and 90° with ease. The magnesium upper and lower guards provide durability while keeping weight low.",
    shortDesc: "M18 FUEL brushless circular saw with superior cutting power and runtime.",
    specs: {
      "Voltage": "18V",
      "Blade Size": "7-1/4 inch",
      "No Load Speed": "5,800 RPM",
      "Bevel Capacity": "0-50°",
      "Weight": "7.2 lbs",
      "Battery": "M18 REDLITHIUM",
      "Warranty": "5 Years",
    },
    rating: 4.9,
    reviewCount: 183,
    badge: "Top Rated",
    inStock: true,
    sku: "SL-SAW-002",
    reviews: [
      { id: "r002a", author: "James Wilson", avatar: "JW", rating: 5, title: "Best circular saw I've owned", comment: "Replaced my corded saw with this and never looked back. The power is incredible and the battery lasts through a full day of framing work.", date: "2024-11-20", verified: true },
      { id: "r002b", author: "Patricia Lee", avatar: "PL", rating: 5, title: "Professional grade tool", comment: "As a professional carpenter, I need reliable tools. This saw has never let me down. The cut quality is exceptional.", date: "2024-10-05", verified: true },
      { id: "r002c", author: "Robert Davis", avatar: "RD", rating: 4, title: "Great saw, heavy but worth it", comment: "Slightly heavier than expected but the power more than makes up for it. Cuts through hardwood like butter.", date: "2024-09-30", verified: true },
    ],
  },
  {
    id: "p003",
    name: "Stanley FatMax 25ft Tape Measure",
    category: "measuring",
    price: 52,
    originalPrice: 65,
    images: [
      "https://readdy.ai/api/search-image?query=Stanley%20FatMax%20tape%20measure%20yellow%20professional%20measuring%20tool%20on%20white%20background%2C%20product%20photography&width=800&height=800&seq=p003a&orientation=squarish",
      "https://readdy.ai/api/search-image?query=tape%20measure%20extended%20blade%20showing%20measurements%20professional%20tool%2C%20white%20background&width=800&height=800&seq=p003b&orientation=squarish",
      "https://readdy.ai/api/search-image?query=tape%20measure%20hook%20end%20detail%20close%20up%20professional%20measuring%20hardware&width=800&height=800&seq=p003c&orientation=squarish",
      "https://readdy.ai/api/search-image?query=tape%20measure%20in%20use%20measuring%20wood%20board%20construction%20work&width=800&height=800&seq=p003d&orientation=squarish",
      "https://readdy.ai/api/search-image?query=tape%20measure%20belt%20clip%20feature%20professional%20contractor%20tool&width=800&height=800&seq=p003e&orientation=squarish",
      "https://readdy.ai/api/search-image?query=tape%20measure%20rubber%20grip%20handle%20ergonomic%20design%20professional%20tool&width=800&height=800&seq=p003f&orientation=squarish",
    ],
    description: "The Stanley FatMax 25ft Tape Measure features a 1-1/4 inch wide blade for maximum standout and durability. The BladeArmor coating provides 10x longer blade life. Features a Mylar polyester film coating for added protection. The large, easy-to-read numbers and true zero hook make accurate measurements simple.",
    shortDesc: "25ft professional tape measure with BladeArmor coating and 11ft standout.",
    specs: {
      "Length": "25 ft / 7.5 m",
      "Blade Width": "1-1/4 inch",
      "Standout": "11 ft",
      "Coating": "BladeArmor",
      "Weight": "0.9 lbs",
      "Lock": "Thumb Lock",
      "Warranty": "Lifetime",
    },
    rating: 4.7,
    reviewCount: 412,
    inStock: true,
    sku: "SL-MSR-003",
    reviews: [
      { id: "r003a", author: "Tom Baker", avatar: "TB", rating: 5, title: "Durable and accurate", comment: "This tape measure has survived job sites, drops, and years of use. The blade is still in great condition thanks to the coating.", date: "2024-11-10", verified: true },
      { id: "r003b", author: "Linda Chen", avatar: "LC", rating: 4, title: "Great for home use", comment: "Very easy to read and the standout is impressive. Perfect for measuring large spaces alone.", date: "2024-10-22", verified: true },
      { id: "r003c", author: "Frank Miller", avatar: "FM", rating: 5, title: "Professional quality", comment: "I've gone through many tape measures over the years. This one is by far the most durable and accurate.", date: "2024-09-18", verified: true },
    ],
  },
  {
    id: "p004",
    name: "Bosch 12V Max Rotary Hammer Drill",
    category: "power-tools",
    price: 279,
    originalPrice: 329,
    images: [
      "https://readdy.ai/api/search-image?query=Bosch%20rotary%20hammer%20drill%20professional%20power%20tool%20blue%20color%20on%20white%20background%2C%20product%20photography&width=800&height=800&seq=p004a&orientation=squarish",
      "https://readdy.ai/api/search-image?query=rotary%20hammer%20drill%20SDS%20chuck%20close%20up%20professional%20masonry%20tool&width=800&height=800&seq=p004b&orientation=squarish",
      "https://readdy.ai/api/search-image?query=hammer%20drill%20drilling%20concrete%20wall%20professional%20contractor%20work&width=800&height=800&seq=p004c&orientation=squarish",
      "https://readdy.ai/api/search-image?query=rotary%20hammer%20drill%20side%20view%20with%20battery%20professional%20cordless%20tool&width=800&height=800&seq=p004d&orientation=squarish",
      "https://readdy.ai/api/search-image?query=hammer%20drill%20accessories%20bits%20set%20professional%20masonry%20hardware&width=800&height=800&seq=p004e&orientation=squarish",
      "https://readdy.ai/api/search-image?query=rotary%20hammer%20drill%20carrying%20case%20professional%20tool%20storage&width=800&height=800&seq=p004f&orientation=squarish",
    ],
    description: "The Bosch 12V Max Rotary Hammer Drill combines compact size with professional performance. Features SDS-plus bit system for quick bit changes. The 3-mode operation (rotary, hammer, chisel) makes it versatile for drilling in concrete, masonry, and wood. Includes 2 batteries and a carrying case.",
    shortDesc: "Compact 12V rotary hammer with SDS-plus system and 3-mode operation.",
    specs: {
      "Voltage": "12V MAX",
      "Chuck": "SDS-plus",
      "Impact Rate": "0-4,500 BPM",
      "Speed": "0-1,100 RPM",
      "Weight": "2.8 lbs",
      "Modes": "3 (Drill/Hammer/Chisel)",
      "Warranty": "3 Years",
    },
    rating: 4.6,
    reviewCount: 156,
    inStock: true,
    sku: "SL-HMR-004",
    reviews: [
      { id: "r004a", author: "David Kim", avatar: "DK", rating: 5, title: "Perfect for masonry work", comment: "Used this on a tile removal project. The chisel mode worked perfectly and the compact size made it easy to maneuver.", date: "2024-11-08", verified: true },
      { id: "r004b", author: "Angela White", avatar: "AW", rating: 4, title: "Great compact hammer drill", comment: "Surprisingly powerful for its size. The SDS system makes bit changes quick and easy.", date: "2024-10-15", verified: true },
      { id: "r004c", author: "Steve Brown", avatar: "SB", rating: 5, title: "Excellent build quality", comment: "Bosch never disappoints. This drill has handled everything I've thrown at it including drilling through brick.", date: "2024-09-25", verified: true },
    ],
  },
  {
    id: "p005",
    name: "Craftsman 230-Piece Mechanics Tool Set",
    category: "hand-tools",
    price: 189,
    originalPrice: 249,
    images: [
      "https://readdy.ai/api/search-image?query=Craftsman%20mechanics%20tool%20set%20organized%20in%20case%20wrenches%20sockets%20professional%20hardware%2C%20white%20background&width=800&height=800&seq=p005a&orientation=squarish",
      "https://readdy.ai/api/search-image?query=socket%20wrench%20set%20organized%20in%20foam%20case%20professional%20mechanics%20tools&width=800&height=800&seq=p005b&orientation=squarish",
      "https://readdy.ai/api/search-image?query=combination%20wrench%20set%20chrome%20vanadium%20professional%20hand%20tools&width=800&height=800&seq=p005c&orientation=squarish",
      "https://readdy.ai/api/search-image?query=ratchet%20handle%20socket%20drive%20professional%20mechanics%20tool%20close%20up&width=800&height=800&seq=p005d&orientation=squarish",
      "https://readdy.ai/api/search-image?query=mechanics%20tool%20set%20in%20use%20automotive%20repair%20professional%20work&width=800&height=800&seq=p005e&orientation=squarish",
      "https://readdy.ai/api/search-image?query=tool%20set%20carrying%20case%20organized%20storage%20professional%20hardware%20kit&width=800&height=800&seq=p005f&orientation=squarish",
    ],
    description: "The Craftsman 230-Piece Mechanics Tool Set includes everything needed for automotive and mechanical work. Features chrome vanadium steel construction for durability. Includes 1/4\", 3/8\", and 1/2\" drive ratchets, sockets, extensions, and combination wrenches. All pieces organized in a blow-molded case.",
    shortDesc: "230-piece comprehensive mechanics set with chrome vanadium steel construction.",
    specs: {
      "Pieces": "230",
      "Drive Sizes": "1/4\", 3/8\", 1/2\"",
      "Material": "Chrome Vanadium Steel",
      "Finish": "Full Polish Chrome",
      "Case": "Blow-Molded",
      "SAE/Metric": "Both",
      "Warranty": "Lifetime",
    },
    rating: 4.7,
    reviewCount: 328,
    badge: "Best Value",
    inStock: true,
    sku: "SL-SET-005",
    reviews: [
      { id: "r005a", author: "Mark Anderson", avatar: "MA", rating: 5, title: "Complete set for any job", comment: "This set has everything I need for car maintenance and home repairs. The quality is excellent for the price.", date: "2024-11-12", verified: true },
      { id: "r005b", author: "Jennifer Taylor", avatar: "JT", rating: 4, title: "Great starter set", comment: "Perfect for someone building their tool collection. Good quality and the case keeps everything organized.", date: "2024-10-30", verified: true },
      { id: "r005c", author: "Chris Martinez", avatar: "CM", rating: 5, title: "Professional quality", comment: "I'm a mechanic and this set holds up to daily professional use. The ratchets are smooth and the sockets fit perfectly.", date: "2024-09-20", verified: true },
    ],
  },
  {
    id: "p006",
    name: "Makita 18V LXT Angle Grinder",
    category: "power-tools",
    price: 219,
    originalPrice: 269,
    images: [
      "https://readdy.ai/api/search-image?query=Makita%20angle%20grinder%20professional%20power%20tool%20teal%20color%20on%20white%20background%2C%20product%20photography&width=800&height=800&seq=p006a&orientation=squarish",
      "https://readdy.ai/api/search-image?query=angle%20grinder%20disc%20guard%20close%20up%20professional%20cutting%20grinding%20tool&width=800&height=800&seq=p006b&orientation=squarish",
      "https://readdy.ai/api/search-image?query=angle%20grinder%20grinding%20metal%20sparks%20professional%20contractor%20work&width=800&height=800&seq=p006c&orientation=squarish",
      "https://readdy.ai/api/search-image?query=angle%20grinder%20side%20handle%20grip%20professional%20ergonomic%20tool%20design&width=800&height=800&seq=p006d&orientation=squarish",
      "https://readdy.ai/api/search-image?query=angle%20grinder%20with%20battery%20pack%20professional%20cordless%20tool&width=800&height=800&seq=p006e&orientation=squarish",
      "https://readdy.ai/api/search-image?query=angle%20grinder%20accessories%20discs%20set%20professional%20hardware%20tools&width=800&height=800&seq=p006f&orientation=squarish",
    ],
    description: "The Makita 18V LXT Angle Grinder features a brushless motor for longer run time and increased power. The 4-1/2\" / 5\" wheel capacity handles a wide range of grinding and cutting applications. Features an electric brake for added safety and a tool-less wheel guard for quick adjustments.",
    shortDesc: "18V brushless angle grinder with electric brake and tool-less guard adjustment.",
    specs: {
      "Voltage": "18V LXT",
      "Wheel Size": "4-1/2\" / 5\"",
      "No Load Speed": "8,500 RPM",
      "Spindle Thread": "5/8\"-11",
      "Weight": "4.4 lbs",
      "Motor": "Brushless",
      "Warranty": "3 Years",
    },
    rating: 4.8,
    reviewCount: 201,
    inStock: true,
    sku: "SL-GRD-006",
    reviews: [
      { id: "r006a", author: "Paul Garcia", avatar: "PG", rating: 5, title: "Powerful and reliable", comment: "This grinder handles everything from cutting rebar to grinding welds. The brushless motor makes a huge difference in runtime.", date: "2024-11-05", verified: true },
      { id: "r006b", author: "Nancy Rodriguez", avatar: "NR", rating: 5, title: "Great for metalwork", comment: "I use this daily in my fabrication shop. It's powerful, well-balanced, and the electric brake is a great safety feature.", date: "2024-10-18", verified: true },
      { id: "r006c", author: "Kevin Lewis", avatar: "KL", rating: 4, title: "Solid grinder", comment: "Good power and the tool-less guard adjustment saves time. Battery life is decent for the work I do.", date: "2024-09-08", verified: true },
    ],
  },
  {
    id: "p007",
    name: "Klein Tools 11-in-1 Screwdriver Set",
    category: "hand-tools",
    price: 68,
    originalPrice: 85,
    images: [
      "https://readdy.ai/api/search-image?query=Klein%20Tools%20multi-bit%20screwdriver%20set%20professional%20electrician%20tool%20on%20white%20background%2C%20product%20photography&width=800&height=800&seq=p007a&orientation=squarish",
      "https://readdy.ai/api/search-image?query=screwdriver%20bits%20set%20organized%20professional%20hand%20tool%20close%20up&width=800&height=800&seq=p007b&orientation=squarish",
      "https://readdy.ai/api/search-image?query=multi-bit%20screwdriver%20handle%20grip%20ergonomic%20professional%20tool&width=800&height=800&seq=p007c&orientation=squarish",
      "https://readdy.ai/api/search-image?query=screwdriver%20in%20use%20electrical%20panel%20work%20professional%20electrician&width=800&height=800&seq=p007d&orientation=squarish",
      "https://readdy.ai/api/search-image?query=screwdriver%20bit%20storage%20handle%20professional%20tool%20design&width=800&height=800&seq=p007e&orientation=squarish",
      "https://readdy.ai/api/search-image?query=screwdriver%20set%20multiple%20tips%20professional%20hardware%20tool%20kit&width=800&height=800&seq=p007f&orientation=squarish",
    ],
    description: "The Klein Tools 11-in-1 Screwdriver features a heavy-duty handle that stores 6 double-ended bits. Includes Phillips #1 and #2, slotted 3/16\" and 1/4\", Torx T10 and T15, and square #1 and #2 bits. The cushion-grip handle provides comfort and torque. Shaft is made from high-alloy steel.",
    shortDesc: "11-in-1 multi-bit screwdriver with cushion-grip handle and high-alloy steel shaft.",
    specs: {
      "Bits Included": "11",
      "Shaft Material": "High-Alloy Steel",
      "Handle": "Cushion-Grip",
      "Bit Types": "Phillips, Slotted, Torx, Square",
      "Length": "9.5 inches",
      "Weight": "0.5 lbs",
      "Warranty": "Lifetime",
    },
    rating: 4.9,
    reviewCount: 567,
    badge: "Top Rated",
    inStock: true,
    sku: "SL-SCR-007",
    reviews: [
      { id: "r007a", author: "Brian Hall", avatar: "BH", rating: 5, title: "Best screwdriver I own", comment: "The versatility of this tool is unmatched. I use it every day as an electrician and it handles everything.", date: "2024-11-18", verified: true },
      { id: "r007b", author: "Susan Clark", avatar: "SC", rating: 5, title: "Perfect for home repairs", comment: "Having 11 different bits in one tool is incredibly convenient. The grip is comfortable and the build quality is excellent.", date: "2024-10-25", verified: true },
      { id: "r007c", author: "Daniel Young", avatar: "DY", rating: 5, title: "Klein quality as always", comment: "Klein Tools never disappoints. This screwdriver is built to last and covers all the common bit types I need.", date: "2024-09-14", verified: true },
    ],
  },
  {
    id: "p008",
    name: "Bosch Laser Level Self-Leveling",
    category: "measuring",
    price: 159,
    originalPrice: 199,
    images: [
      "https://readdy.ai/api/search-image?query=Bosch%20laser%20level%20self-leveling%20professional%20measuring%20tool%20on%20white%20background%2C%20product%20photography&width=800&height=800&seq=p008a&orientation=squarish",
      "https://readdy.ai/api/search-image?query=laser%20level%20projecting%20red%20line%20on%20wall%20professional%20measuring%20tool&width=800&height=800&seq=p008b&orientation=squarish",
      "https://readdy.ai/api/search-image?query=laser%20level%20tripod%20mount%20professional%20surveying%20tool%20close%20up&width=800&height=800&seq=p008c&orientation=squarish",
      "https://readdy.ai/api/search-image?query=laser%20level%20in%20use%20hanging%20pictures%20home%20improvement%20professional&width=800&height=800&seq=p008d&orientation=squarish",
      "https://readdy.ai/api/search-image?query=laser%20level%20carrying%20case%20accessories%20professional%20measuring%20kit&width=800&height=800&seq=p008e&orientation=squarish",
      "https://readdy.ai/api/search-image?query=laser%20level%20display%20panel%20controls%20professional%20measuring%20device&width=800&height=800&seq=p008f&orientation=squarish",
    ],
    description: "The Bosch Self-Leveling Cross-Line Laser Level projects bright, highly visible horizontal and vertical lines for a wide range of leveling and alignment applications. Self-leveling within 4° for quick setup. The VisiMax technology optimizes laser visibility and battery life. Works up to 65 feet.",
    shortDesc: "Self-leveling cross-line laser with VisiMax technology, 65ft range.",
    specs: {
      "Range": "65 ft",
      "Self-Leveling": "Within 4°",
      "Accuracy": "±1/8\" at 30 ft",
      "Lines": "Horizontal + Vertical",
      "Battery": "3x AA",
      "IP Rating": "IP54",
      "Warranty": "2 Years",
    },
    rating: 4.7,
    reviewCount: 289,
    inStock: true,
    sku: "SL-LVL-008",
    reviews: [
      { id: "r008a", author: "Eric Scott", avatar: "ES", rating: 5, title: "Accurate and easy to use", comment: "Used this for tiling a bathroom. The self-leveling feature made setup quick and the lines were perfectly straight.", date: "2024-11-02", verified: true },
      { id: "r008b", author: "Michelle Adams", avatar: "MA", rating: 4, title: "Great for home projects", comment: "Very accurate and the bright laser is easy to see. The carrying case is a nice touch.", date: "2024-10-12", verified: true },
      { id: "r008c", author: "Gary Nelson", avatar: "GN", rating: 5, title: "Professional results", comment: "This level has made my renovation projects so much easier. The accuracy is spot on and it's very easy to set up.", date: "2024-09-22", verified: true },
    ],
  },
  {
    id: "p009",
    name: "Werner 6ft Fiberglass Step Ladder",
    category: "safety",
    price: 129,
    originalPrice: 159,
    images: [
      "https://readdy.ai/api/search-image?query=Werner%20fiberglass%20step%20ladder%20professional%20safety%20equipment%20on%20white%20background%2C%20product%20photography&width=800&height=800&seq=p009a&orientation=squarish",
      "https://readdy.ai/api/search-image?query=step%20ladder%20top%20platform%20detail%20professional%20safety%20hardware&width=800&height=800&seq=p009b&orientation=squarish",
      "https://readdy.ai/api/search-image?query=fiberglass%20ladder%20non-conductive%20professional%20electrician%20safety%20tool&width=800&height=800&seq=p009c&orientation=squarish",
      "https://readdy.ai/api/search-image?query=step%20ladder%20in%20use%20painting%20ceiling%20home%20improvement%20work&width=800&height=800&seq=p009d&orientation=squarish",
      "https://readdy.ai/api/search-image?query=ladder%20feet%20non-slip%20rubber%20safety%20feature%20close%20up&width=800&height=800&seq=p009e&orientation=squarish",
      "https://readdy.ai/api/search-image?query=step%20ladder%20folded%20storage%20compact%20professional%20safety%20equipment&width=800&height=800&seq=p009f&orientation=squarish",
    ],
    description: "The Werner 6ft Fiberglass Step Ladder is rated Type IA (300 lb capacity) for heavy-duty professional use. The non-conductive fiberglass rails make it safe for electrical work. Features slip-resistant TRACTION-TRED steps and a large top with tool slots. ANSI/OSHA compliant.",
    shortDesc: "6ft Type IA fiberglass ladder, 300lb capacity, non-conductive for electrical work.",
    specs: {
      "Height": "6 ft",
      "Capacity": "300 lbs (Type IA)",
      "Material": "Fiberglass",
      "Steps": "5",
      "Weight": "18 lbs",
      "Compliance": "ANSI/OSHA",
      "Warranty": "Lifetime",
    },
    rating: 4.8,
    reviewCount: 198,
    inStock: true,
    sku: "SL-LAD-009",
    reviews: [
      { id: "r009a", author: "Larry Turner", avatar: "LT", rating: 5, title: "Sturdy and safe", comment: "This ladder feels incredibly solid. The non-slip steps give great confidence when working at height.", date: "2024-11-14", verified: true },
      { id: "r009b", author: "Donna Phillips", avatar: "DP", rating: 5, title: "Perfect for electrical work", comment: "The fiberglass construction is essential for my electrical work. This ladder is well-built and very stable.", date: "2024-10-08", verified: true },
      { id: "r009c", author: "Raymond Campbell", avatar: "RC", rating: 4, title: "Good quality ladder", comment: "Solid construction and the 300lb rating gives peace of mind. A bit heavy but that's expected with fiberglass.", date: "2024-09-16", verified: true },
    ],
  },
  {
    id: "p010",
    name: "Milwaukee PACKOUT 3-Drawer Tool Chest",
    category: "storage",
    price: 449,
    originalPrice: 549,
    images: [
      "https://readdy.ai/api/search-image?query=Milwaukee%20PACKOUT%20tool%20chest%20storage%20system%20red%20professional%20hardware%20on%20white%20background&width=800&height=800&seq=p010a&orientation=squarish",
      "https://readdy.ai/api/search-image?query=tool%20chest%20drawers%20open%20organized%20tools%20storage%20professional%20system&width=800&height=800&seq=p010b&orientation=squarish",
      "https://readdy.ai/api/search-image?query=modular%20tool%20storage%20system%20stackable%20professional%20hardware%20organization&width=800&height=800&seq=p010c&orientation=squarish",
      "https://readdy.ai/api/search-image?query=tool%20chest%20wheels%20casters%20professional%20mobile%20storage%20system&width=800&height=800&seq=p010d&orientation=squarish",
      "https://readdy.ai/api/search-image?query=tool%20chest%20lock%20security%20feature%20professional%20storage%20hardware&width=800&height=800&seq=p010e&orientation=squarish",
      "https://readdy.ai/api/search-image?query=tool%20storage%20system%20full%20setup%20professional%20workshop%20organization&width=800&height=800&seq=p010f&orientation=squarish",
    ],
    description: "The Milwaukee PACKOUT 3-Drawer Tool Chest is the ultimate modular storage solution. Features 3 full-extension drawers with 100 lb capacity each. The IP65 rated weather seal protects tools from dust and water. Compatible with the entire PACKOUT system. Heavy-duty casters for easy mobility.",
    shortDesc: "PACKOUT modular 3-drawer chest with IP65 rating and 100lb drawer capacity.",
    specs: {
      "Drawers": "3 Full-Extension",
      "Drawer Capacity": "100 lbs each",
      "IP Rating": "IP65",
      "Material": "Steel + Polymer",
      "Casters": "4 Heavy-Duty",
      "Compatibility": "PACKOUT System",
      "Warranty": "Lifetime",
    },
    rating: 4.9,
    reviewCount: 142,
    badge: "Premium",
    inStock: true,
    sku: "SL-STR-010",
    reviews: [
      { id: "r010a", author: "Timothy Evans", avatar: "TE", rating: 5, title: "Best tool storage system", comment: "The PACKOUT system is incredible. Everything locks together and the IP65 rating means I can leave it in my truck bed.", date: "2024-11-22", verified: true },
      { id: "r010b", author: "Barbara Collins", avatar: "BC", rating: 5, title: "Worth every penny", comment: "Expensive but absolutely worth it. The build quality is exceptional and the drawers slide smoothly even when fully loaded.", date: "2024-10-20", verified: true },
      { id: "r010c", author: "Kenneth Stewart", avatar: "KS", rating: 4, title: "Great storage solution", comment: "Very well built and the modular system is genius. The only downside is the price, but you get what you pay for.", date: "2024-09-28", verified: true },
    ],
  },
  {
    id: "p011",
    name: "3M Safety Glasses Anti-Fog Clear Lens",
    category: "safety",
    price: 58,
    originalPrice: 72,
    images: [
      "https://readdy.ai/api/search-image?query=3M%20safety%20glasses%20clear%20lens%20anti-fog%20professional%20eye%20protection%20on%20white%20background&width=800&height=800&seq=p011a&orientation=squarish",
      "https://readdy.ai/api/search-image?query=safety%20glasses%20side%20view%20wraparound%20protection%20professional%20hardware&width=800&height=800&seq=p011b&orientation=squarish",
      "https://readdy.ai/api/search-image?query=safety%20glasses%20in%20use%20construction%20work%20professional%20eye%20protection&width=800&height=800&seq=p011c&orientation=squarish",
      "https://readdy.ai/api/search-image?query=safety%20glasses%20lens%20close%20up%20anti-scratch%20coating%20professional&width=800&height=800&seq=p011d&orientation=squarish",
      "https://readdy.ai/api/search-image?query=safety%20glasses%20temple%20arm%20adjustment%20professional%20ergonomic%20design&width=800&height=800&seq=p011e&orientation=squarish",
      "https://readdy.ai/api/search-image?query=safety%20glasses%20case%20storage%20professional%20eye%20protection%20kit&width=800&height=800&seq=p011f&orientation=squarish",
    ],
    description: "The 3M Safety Glasses feature Scotchgard Anti-Fog coating for clear vision in humid conditions. The wraparound design provides 99.9% UV protection. Lightweight frame with adjustable temples for all-day comfort. ANSI Z87.1 certified for impact protection.",
    shortDesc: "Anti-fog safety glasses with UV protection and ANSI Z87.1 certification.",
    specs: {
      "Lens": "Clear Anti-Fog",
      "UV Protection": "99.9%",
      "Certification": "ANSI Z87.1",
      "Frame": "Lightweight Nylon",
      "Temples": "Adjustable",
      "Weight": "0.8 oz",
      "Warranty": "1 Year",
    },
    rating: 4.6,
    reviewCount: 334,
    inStock: true,
    sku: "SL-SAF-011",
    reviews: [
      { id: "r011a", author: "George Morris", avatar: "GM", rating: 5, title: "Clear and comfortable", comment: "These glasses stay fog-free even when I'm working hard. Very comfortable for all-day wear.", date: "2024-11-09", verified: true },
      { id: "r011b", author: "Helen Rogers", avatar: "HR", rating: 4, title: "Good eye protection", comment: "Lightweight and comfortable. The anti-fog coating works well. Good value for the price.", date: "2024-10-17", verified: true },
      { id: "r011c", author: "Walter Reed", avatar: "WR", rating: 5, title: "Essential safety gear", comment: "I wear these every day on the job site. They're comfortable, clear, and the anti-fog coating is excellent.", date: "2024-09-05", verified: true },
    ],
  },
  {
    id: "p012",
    name: "Estwing 20oz Framing Hammer",
    category: "hand-tools",
    price: 79,
    originalPrice: 95,
    images: [
      "https://readdy.ai/api/search-image?query=Estwing%20framing%20hammer%20professional%20hand%20tool%20steel%20handle%20on%20white%20background%2C%20product%20photography&width=800&height=800&seq=p012a&orientation=squarish",
      "https://readdy.ai/api/search-image?query=framing%20hammer%20head%20close%20up%20waffle%20face%20professional%20carpentry%20tool&width=800&height=800&seq=p012b&orientation=squarish",
      "https://readdy.ai/api/search-image?query=hammer%20in%20use%20framing%20house%20construction%20professional%20carpenter%20work&width=800&height=800&seq=p012c&orientation=squarish",
      "https://readdy.ai/api/search-image?query=framing%20hammer%20grip%20handle%20ergonomic%20professional%20hand%20tool&width=800&height=800&seq=p012d&orientation=squarish",
      "https://readdy.ai/api/search-image?query=hammer%20nail%20puller%20claw%20feature%20professional%20carpentry%20hardware&width=800&height=800&seq=p012e&orientation=squarish",
      "https://readdy.ai/api/search-image?query=framing%20hammer%20weight%20balance%20professional%20construction%20tool&width=800&height=800&seq=p012f&orientation=squarish",
    ],
    description: "The Estwing 20oz Framing Hammer is made in the USA from a single piece of steel for maximum strength. The patented Shock Reduction Grip reduces vibration by 70%. The waffle face provides superior nail driving grip. The rip claw is perfect for prying and demolition work.",
    shortDesc: "20oz one-piece steel framing hammer with 70% vibration reduction grip.",
    specs: {
      "Weight": "20 oz",
      "Material": "One-Piece Steel",
      "Face": "Waffle",
      "Claw": "Rip",
      "Handle": "Shock Reduction Grip",
      "Length": "16 inches",
      "Warranty": "Lifetime",
    },
    rating: 4.9,
    reviewCount: 445,
    badge: "Made in USA",
    inStock: true,
    sku: "SL-HMR-012",
    reviews: [
      { id: "r012a", author: "Joseph Cook", avatar: "JC", rating: 5, title: "Best hammer ever made", comment: "I've been framing houses for 20 years and this is the only hammer I use. The balance is perfect and it drives nails effortlessly.", date: "2024-11-16", verified: true },
      { id: "r012b", author: "Dorothy Bailey", avatar: "DB", rating: 5, title: "Excellent quality", comment: "The shock reduction grip really works. My arm doesn't get tired even after a full day of nailing.", date: "2024-10-24", verified: true },
      { id: "r012c", author: "Charles Rivera", avatar: "CR", rating: 5, title: "Worth the investment", comment: "More expensive than other hammers but the quality is unmatched. This will last a lifetime.", date: "2024-09-11", verified: true },
    ],
  },
  {
    id: "p013",
    name: "Ridgid 10-inch Table Saw",
    category: "power-tools",
    price: 599,
    originalPrice: 749,
    images: [
      "https://readdy.ai/api/search-image?query=Ridgid%20table%20saw%20professional%20woodworking%20power%20tool%20on%20white%20background%2C%20product%20photography&width=800&height=800&seq=p013a&orientation=squarish",
      "https://readdy.ai/api/search-image?query=table%20saw%20blade%20guard%20safety%20feature%20professional%20woodworking%20tool&width=800&height=800&seq=p013b&orientation=squarish",
      "https://readdy.ai/api/search-image?query=table%20saw%20fence%20rip%20guide%20professional%20woodworking%20equipment&width=800&height=800&seq=p013c&orientation=squarish",
      "https://readdy.ai/api/search-image?query=table%20saw%20cutting%20wood%20board%20professional%20carpenter%20workshop&width=800&height=800&seq=p013d&orientation=squarish",
      "https://readdy.ai/api/search-image?query=table%20saw%20miter%20gauge%20angle%20cuts%20professional%20woodworking&width=800&height=800&seq=p013e&orientation=squarish",
      "https://readdy.ai/api/search-image?query=table%20saw%20stand%20wheels%20mobile%20professional%20workshop%20equipment&width=800&height=800&seq=p013f&orientation=squarish",
    ],
    description: "The Ridgid 10-inch Table Saw delivers professional results with a powerful 15-amp motor. The rack and pinion fence system provides accurate, repeatable cuts. The 30-inch rip capacity handles large sheet goods. Includes a mobile stand with wheels for easy transport around the job site.",
    shortDesc: "10-inch table saw with 15-amp motor, 30-inch rip capacity, and mobile stand.",
    specs: {
      "Blade Size": "10 inch",
      "Motor": "15 Amp",
      "Rip Capacity": "30 inches",
      "Bevel": "0-45°",
      "Speed": "4,300 RPM",
      "Weight": "100 lbs",
      "Warranty": "Lifetime Service",
    },
    rating: 4.7,
    reviewCount: 167,
    badge: "Professional",
    inStock: true,
    sku: "SL-SAW-013",
    reviews: [
      { id: "r013a", author: "Edward Murphy", avatar: "EM", rating: 5, title: "Excellent table saw", comment: "The fence system is incredibly accurate and the motor has plenty of power for hardwoods. Great saw for the price.", date: "2024-11-07", verified: true },
      { id: "r013b", author: "Frances Bell", avatar: "FB", rating: 4, title: "Good for the workshop", comment: "Solid table saw with good features. The mobile stand is very convenient. Setup took about an hour.", date: "2024-10-14", verified: true },
      { id: "r013c", author: "Harold Cooper", avatar: "HC", rating: 5, title: "Professional quality cuts", comment: "I've made hundreds of cuts with this saw and it's still as accurate as day one. The lifetime service warranty is a great bonus.", date: "2024-09-03", verified: true },
    ],
  },
  {
    id: "p014",
    name: "Channellock 8-Piece Pliers Set",
    category: "hand-tools",
    price: 119,
    originalPrice: 149,
    images: [
      "https://readdy.ai/api/search-image?query=Channellock%20pliers%20set%20professional%20hand%20tools%20on%20white%20background%2C%20product%20photography&width=800&height=800&seq=p014a&orientation=squarish",
      "https://readdy.ai/api/search-image?query=needle%20nose%20pliers%20close%20up%20professional%20hand%20tool%20detail&width=800&height=800&seq=p014b&orientation=squarish",
      "https://readdy.ai/api/search-image?query=pliers%20set%20organized%20professional%20hand%20tools%20various%20types&width=800&height=800&seq=p014c&orientation=squarish",
      "https://readdy.ai/api/search-image?query=pliers%20in%20use%20electrical%20work%20professional%20contractor%20tool&width=800&height=800&seq=p014d&orientation=squarish",
      "https://readdy.ai/api/search-image?query=pliers%20grip%20handle%20comfort%20professional%20hand%20tool%20ergonomic&width=800&height=800&seq=p014e&orientation=squarish",
      "https://readdy.ai/api/search-image?query=pliers%20set%20storage%20roll%20pouch%20professional%20tool%20organization&width=800&height=800&seq=p014f&orientation=squarish",
    ],
    description: "The Channellock 8-Piece Pliers Set includes the most commonly used pliers for professional and home use. Made from high carbon C1080 steel with a proprietary Channellock Blue comfort grip. Includes slip joint, long nose, diagonal cutting, tongue and groove, and lineman's pliers.",
    shortDesc: "8-piece professional pliers set with C1080 steel and comfort grip handles.",
    specs: {
      "Pieces": "8",
      "Material": "C1080 High Carbon Steel",
      "Finish": "Laser Heat Treated",
      "Grip": "Channellock Blue",
      "Types": "Slip Joint, Long Nose, Diagonal, T&G, Lineman's",
      "Made In": "USA",
      "Warranty": "Lifetime",
    },
    rating: 4.8,
    reviewCount: 276,
    inStock: true,
    sku: "SL-PLR-014",
    reviews: [
      { id: "r014a", author: "Irene Richardson", avatar: "IR", rating: 5, title: "Best pliers I've used", comment: "The quality of these pliers is outstanding. The grip is comfortable and they're incredibly strong.", date: "2024-11-19", verified: true },
      { id: "r014b", author: "Jack Watson", avatar: "JW", rating: 5, title: "Professional grade", comment: "I'm a plumber and these pliers handle everything I throw at them. The tongue and groove pliers are especially good.", date: "2024-10-27", verified: true },
      { id: "r014c", author: "Karen Brooks", avatar: "KB", rating: 4, title: "Great set", comment: "Good quality pliers that cover all the basics. The comfort grip makes a big difference during long jobs.", date: "2024-09-15", verified: true },
    ],
  },
  {
    id: "p015",
    name: "Fluke 117 Electrician's Multimeter",
    category: "measuring",
    price: 189,
    originalPrice: 229,
    images: [
      "https://readdy.ai/api/search-image?query=Fluke%20multimeter%20professional%20electrical%20measuring%20tool%20yellow%20on%20white%20background%2C%20product%20photography&width=800&height=800&seq=p015a&orientation=squarish",
      "https://readdy.ai/api/search-image?query=multimeter%20display%20reading%20voltage%20professional%20electrical%20tool&width=800&height=800&seq=p015b&orientation=squarish",
      "https://readdy.ai/api/search-image?query=multimeter%20probes%20test%20leads%20professional%20electrical%20measurement&width=800&height=800&seq=p015c&orientation=squarish",
      "https://readdy.ai/api/search-image?query=multimeter%20in%20use%20testing%20electrical%20panel%20professional%20electrician&width=800&height=800&seq=p015d&orientation=squarish",
      "https://readdy.ai/api/search-image?query=multimeter%20dial%20selector%20professional%20electrical%20measuring%20device&width=800&height=800&seq=p015e&orientation=squarish",
      "https://readdy.ai/api/search-image?query=multimeter%20carrying%20case%20accessories%20professional%20electrical%20kit&width=800&height=800&seq=p015f&orientation=squarish",
    ],
    description: "The Fluke 117 Electrician's Multimeter is designed specifically for commercial electricians. Features AutoVolt automatic AC/DC voltage selection and Non-Contact Voltage detection. The large backlit display is easy to read in any lighting. CAT III 600V safety rated for professional use.",
    shortDesc: "Professional electrician's multimeter with AutoVolt and non-contact voltage detection.",
    specs: {
      "Voltage AC": "600V",
      "Voltage DC": "600V",
      "Current": "10A",
      "Resistance": "40 MΩ",
      "Safety": "CAT III 600V",
      "Display": "6000 Count Backlit",
      "Warranty": "3 Years",
    },
    rating: 4.9,
    reviewCount: 389,
    badge: "Professional",
    inStock: true,
    sku: "SL-ELC-015",
    reviews: [
      { id: "r015a", author: "Lloyd Sanders", avatar: "LS", rating: 5, title: "Essential electrician tool", comment: "The AutoVolt feature is incredibly convenient. This meter is accurate, reliable, and built to last.", date: "2024-11-11", verified: true },
      { id: "r015b", author: "Mildred Price", avatar: "MP", rating: 5, title: "Best multimeter available", comment: "I've used many multimeters over my 25-year career. The Fluke 117 is the best I've ever used.", date: "2024-10-19", verified: true },
      { id: "r015c", author: "Norman Hughes", avatar: "NH", rating: 5, title: "Worth every dollar", comment: "Expensive but absolutely worth it. The non-contact voltage detection has saved me from dangerous situations.", date: "2024-09-07", verified: true },
    ],
  },
  {
    id: "p016",
    name: "DeWalt 20V Jigsaw Cordless",
    category: "power-tools",
    price: 169,
    originalPrice: 209,
    images: [
      "https://readdy.ai/api/search-image?query=DeWalt%20cordless%20jigsaw%20professional%20power%20tool%20yellow%20on%20white%20background%2C%20product%20photography&width=800&height=800&seq=p016a&orientation=squarish",
      "https://readdy.ai/api/search-image?query=jigsaw%20blade%20close%20up%20professional%20cutting%20tool%20detail&width=800&height=800&seq=p016b&orientation=squarish",
      "https://readdy.ai/api/search-image?query=jigsaw%20cutting%20curved%20wood%20professional%20carpenter%20work&width=800&height=800&seq=p016c&orientation=squarish",
      "https://readdy.ai/api/search-image?query=jigsaw%20base%20plate%20bevel%20adjustment%20professional%20tool%20feature&width=800&height=800&seq=p016d&orientation=squarish",
      "https://readdy.ai/api/search-image?query=jigsaw%20with%20battery%20pack%20professional%20cordless%20tool&width=800&height=800&seq=p016e&orientation=squarish",
      "https://readdy.ai/api/search-image?query=jigsaw%20orbital%20action%20setting%20professional%20cutting%20tool&width=800&height=800&seq=p016f&orientation=squarish",
    ],
    description: "The DeWalt 20V Cordless Jigsaw features a 4-position orbital action for aggressive cutting in wood. The tool-free blade change system allows for quick blade swaps. The keyless beveling shoe adjusts from 0-45° for angled cuts. The bright LED light illuminates the work area.",
    shortDesc: "20V cordless jigsaw with 4-position orbital action and tool-free blade change.",
    specs: {
      "Voltage": "20V MAX",
      "Stroke Length": "1 inch",
      "Strokes Per Min": "0-3,000 SPM",
      "Bevel": "0-45°",
      "Orbital Settings": "4",
      "Weight": "4.0 lbs",
      "Warranty": "3 Years",
    },
    rating: 4.7,
    reviewCount: 213,
    inStock: true,
    sku: "SL-JIG-016",
    reviews: [
      { id: "r016a", author: "Oscar Foster", avatar: "OF", rating: 5, title: "Great jigsaw", comment: "The orbital action makes cutting through thick wood much faster. The tool-free blade change is very convenient.", date: "2024-11-04", verified: true },
      { id: "r016b", author: "Phyllis Gonzalez", avatar: "PG", rating: 4, title: "Good cordless jigsaw", comment: "Cuts well and the battery life is decent. The LED light is a nice feature for working in dark areas.", date: "2024-10-11", verified: true },
      { id: "r016c", author: "Quentin Bryant", avatar: "QB", rating: 5, title: "Excellent for woodworking", comment: "I use this for all my curved cuts. The variable speed and orbital settings give great control.", date: "2024-09-19", verified: true },
    ],
  },
  {
    id: "p017",
    name: "Irwin 6-Piece Chisel Set",
    category: "hand-tools",
    price: 89,
    originalPrice: 109,
    images: [
      "https://readdy.ai/api/search-image?query=Irwin%20wood%20chisel%20set%20professional%20hand%20tools%20on%20white%20background%2C%20product%20photography&width=800&height=800&seq=p017a&orientation=squarish",
      "https://readdy.ai/api/search-image?query=chisel%20blade%20edge%20sharp%20professional%20woodworking%20tool%20close%20up&width=800&height=800&seq=p017b&orientation=squarish",
      "https://readdy.ai/api/search-image?query=chisel%20set%20organized%20professional%20woodworking%20hand%20tools&width=800&height=800&seq=p017c&orientation=squarish",
      "https://readdy.ai/api/search-image?query=chisel%20in%20use%20mortise%20woodworking%20professional%20carpenter%20work&width=800&height=800&seq=p017d&orientation=squarish",
      "https://readdy.ai/api/search-image?query=chisel%20handle%20grip%20professional%20woodworking%20tool%20ergonomic&width=800&height=800&seq=p017e&orientation=squarish",
      "https://readdy.ai/api/search-image?query=chisel%20set%20storage%20roll%20professional%20woodworking%20tool%20kit&width=800&height=800&seq=p017f&orientation=squarish",
    ],
    description: "The Irwin 6-Piece Chisel Set features chrome vanadium steel blades for superior edge retention. The tri-material handle provides comfort and durability. Each chisel comes pre-sharpened and ready to use. Includes 1/4\", 3/8\", 1/2\", 3/4\", 1\", and 1-1/4\" sizes.",
    shortDesc: "6-piece chrome vanadium chisel set, pre-sharpened with tri-material handles.",
    specs: {
      "Pieces": "6",
      "Sizes": "1/4\" to 1-1/4\"",
      "Material": "Chrome Vanadium Steel",
      "Handle": "Tri-Material",
      "Pre-Sharpened": "Yes",
      "Strike Cap": "Steel",
      "Warranty": "Lifetime",
    },
    rating: 4.6,
    reviewCount: 178,
    inStock: true,
    sku: "SL-CSL-017",
    reviews: [
      { id: "r017a", author: "Ralph Simmons", avatar: "RS", rating: 5, title: "Sharp and durable", comment: "These chisels came razor sharp and have held their edge well. The handles are comfortable for extended use.", date: "2024-11-13", verified: true },
      { id: "r017b", author: "Sandra Patterson", avatar: "SP", rating: 4, title: "Good quality chisels", comment: "Well-made chisels that cover all the common sizes. The chrome vanadium steel is noticeably better than cheaper options.", date: "2024-10-21", verified: true },
      { id: "r017c", author: "Theodore Alexander", avatar: "TA", rating: 5, title: "Professional woodworking tools", comment: "I use these for furniture making and they perform excellently. The edge retention is impressive.", date: "2024-09-09", verified: true },
    ],
  },
  {
    id: "p018",
    name: "Bosch 18V Reciprocating Saw",
    category: "power-tools",
    price: 239,
    originalPrice: 289,
    images: [
      "https://readdy.ai/api/search-image?query=Bosch%20reciprocating%20saw%20professional%20power%20tool%20blue%20on%20white%20background%2C%20product%20photography&width=800&height=800&seq=p018a&orientation=squarish",
      "https://readdy.ai/api/search-image?query=reciprocating%20saw%20blade%20close%20up%20professional%20demolition%20tool&width=800&height=800&seq=p018b&orientation=squarish",
      "https://readdy.ai/api/search-image?query=reciprocating%20saw%20cutting%20pipe%20demolition%20professional%20contractor%20work&width=800&height=800&seq=p018c&orientation=squarish",
      "https://readdy.ai/api/search-image?query=reciprocating%20saw%20shoe%20pivot%20professional%20tool%20feature&width=800&height=800&seq=p018d&orientation=squarish",
      "https://readdy.ai/api/search-image?query=reciprocating%20saw%20with%20battery%20professional%20cordless%20demolition%20tool&width=800&height=800&seq=p018e&orientation=squarish",
      "https://readdy.ai/api/search-image?query=reciprocating%20saw%20blade%20change%20tool-free%20professional%20feature&width=800&height=800&seq=p018f&orientation=squarish",
    ],
    description: "The Bosch 18V Reciprocating Saw delivers up to 3,000 SPM for fast cutting through wood, metal, and PVC. The anti-vibration system reduces vibration by 50% for user comfort. The pivoting shoe adjusts for optimal blade contact. Tool-free blade change for quick swaps.",
    shortDesc: "18V cordless reciprocating saw with anti-vibration system and 3,000 SPM.",
    specs: {
      "Voltage": "18V",
      "Strokes Per Min": "0-3,000 SPM",
      "Stroke Length": "1-1/8 inch",
      "Anti-Vibration": "50% Reduction",
      "Shoe": "Pivoting",
      "Weight": "6.4 lbs",
      "Warranty": "3 Years",
    },
    rating: 4.7,
    reviewCount: 195,
    inStock: true,
    sku: "SL-RCP-018",
    reviews: [
      { id: "r018a", author: "Ursula Henderson", avatar: "UH", rating: 5, title: "Great demolition tool", comment: "This saw cuts through everything. The anti-vibration system makes it much more comfortable to use for extended periods.", date: "2024-11-06", verified: true },
      { id: "r018b", author: "Victor Coleman", avatar: "VC", rating: 4, title: "Powerful and versatile", comment: "Good saw for demolition work. Cuts through wood, metal, and PVC with ease. Battery life is good.", date: "2024-10-13", verified: true },
      { id: "r018c", author: "Wanda Jenkins", avatar: "WJ", rating: 5, title: "Essential contractor tool", comment: "I use this on every job site. The pivoting shoe is very useful for flush cuts and the blade change is quick.", date: "2024-09-21", verified: true },
    ],
  },
  {
    id: "p019",
    name: "Stanley 24-inch Level",
    category: "measuring",
    price: 65,
    originalPrice: 82,
    images: [
      "https://readdy.ai/api/search-image?query=Stanley%20level%20professional%20measuring%20tool%20aluminum%20on%20white%20background%2C%20product%20photography&width=800&height=800&seq=p019a&orientation=squarish",
      "https://readdy.ai/api/search-image?query=level%20vial%20bubble%20close%20up%20professional%20measuring%20tool%20detail&width=800&height=800&seq=p019b&orientation=squarish",
      "https://readdy.ai/api/search-image?query=level%20in%20use%20checking%20wall%20plumb%20professional%20contractor%20work&width=800&height=800&seq=p019c&orientation=squarish",
      "https://readdy.ai/api/search-image?query=level%20aluminum%20body%20professional%20measuring%20tool%20side%20view&width=800&height=800&seq=p019d&orientation=squarish",
      "https://readdy.ai/api/search-image?query=level%20magnetic%20feature%20professional%20measuring%20tool&width=800&height=800&seq=p019e&orientation=squarish",
      "https://readdy.ai/api/search-image?query=level%20end%20cap%20rubber%20protection%20professional%20measuring%20tool&width=800&height=800&seq=p019f&orientation=squarish",
    ],
    description: "The Stanley 24-inch Level features a professional-grade aluminum frame with 3 acrylic vials for horizontal, vertical, and 45° measurements. The magnetic base holds to metal surfaces for hands-free use. Rubber end caps protect against drops. Accurate to 0.5mm/m.",
    shortDesc: "24-inch aluminum level with magnetic base and 3 acrylic vials, 0.5mm/m accuracy.",
    specs: {
      "Length": "24 inches",
      "Material": "Aluminum",
      "Vials": "3 (H, V, 45°)",
      "Accuracy": "0.5mm/m",
      "Magnetic": "Yes",
      "End Caps": "Rubber",
      "Warranty": "Lifetime",
    },
    rating: 4.7,
    reviewCount: 321,
    inStock: true,
    sku: "SL-LVL-019",
    reviews: [
      { id: "r019a", author: "Xavier Perry", avatar: "XP", rating: 5, title: "Accurate and durable", comment: "This level is very accurate and the magnetic base is incredibly useful when working with metal studs.", date: "2024-11-17", verified: true },
      { id: "r019b", author: "Yvonne Powell", avatar: "YP", rating: 4, title: "Good quality level", comment: "Solid aluminum construction and accurate readings. The rubber end caps have saved it from a few drops.", date: "2024-10-26", verified: true },
      { id: "r019c", author: "Zachary Long", avatar: "ZL", rating: 5, title: "Essential tool", comment: "I use this level every day. It's accurate, durable, and the magnetic feature is very handy.", date: "2024-09-13", verified: true },
    ],
  },
  {
    id: "p020",
    name: "Ridgid 12-inch Pipe Wrench",
    category: "hand-tools",
    price: 72,
    originalPrice: 89,
    images: [
      "https://readdy.ai/api/search-image?query=Ridgid%20pipe%20wrench%20professional%20plumbing%20tool%20orange%20on%20white%20background%2C%20product%20photography&width=800&height=800&seq=p020a&orientation=squarish",
      "https://readdy.ai/api/search-image?query=pipe%20wrench%20jaw%20teeth%20close%20up%20professional%20plumbing%20tool&width=800&height=800&seq=p020b&orientation=squarish",
      "https://readdy.ai/api/search-image?query=pipe%20wrench%20in%20use%20plumbing%20work%20professional%20contractor&width=800&height=800&seq=p020c&orientation=squarish",
      "https://readdy.ai/api/search-image?query=pipe%20wrench%20adjustment%20nut%20professional%20plumbing%20hardware&width=800&height=800&seq=p020d&orientation=squarish",
      "https://readdy.ai/api/search-image?query=pipe%20wrench%20handle%20grip%20professional%20plumbing%20tool%20ergonomic&width=800&height=800&seq=p020e&orientation=squarish",
      "https://readdy.ai/api/search-image?query=pipe%20wrench%20set%20different%20sizes%20professional%20plumbing%20tools&width=800&height=800&seq=p020f&orientation=squarish",
    ],
    description: "The Ridgid 12-inch Pipe Wrench is the industry standard for plumbing professionals. Features a drop-forged hook jaw and heel jaw for maximum gripping power. The I-beam handle provides strength while reducing weight. The quick-adjust nut allows for fast size changes.",
    shortDesc: "12-inch drop-forged pipe wrench with I-beam handle and quick-adjust nut.",
    specs: {
      "Length": "12 inches",
      "Capacity": "1-1/2 inch pipe",
      "Material": "Drop-Forged Steel",
      "Handle": "I-Beam",
      "Jaw": "Hook and Heel",
      "Weight": "2.1 lbs",
      "Warranty": "Lifetime",
    },
    rating: 4.8,
    reviewCount: 267,
    inStock: true,
    sku: "SL-WRN-020",
    reviews: [
      { id: "r020a", author: "Aaron Hughes", avatar: "AH", rating: 5, title: "Industry standard for a reason", comment: "Ridgid pipe wrenches are what every plumber uses. The quality is unmatched and they last forever.", date: "2024-11-21", verified: true },
      { id: "r020b", author: "Betty Foster", avatar: "BF", rating: 5, title: "Excellent grip", comment: "The jaw teeth grip pipe incredibly well without slipping. The quick-adjust feature saves time on the job.", date: "2024-10-29", verified: true },
      { id: "r020c", author: "Carl Simmons", avatar: "CS", rating: 4, title: "Good pipe wrench", comment: "Solid construction and good grip. The I-beam handle is comfortable and strong.", date: "2024-09-17", verified: true },
    ],
  },
  {
    id: "p021",
    name: "Makita 18V Random Orbital Sander",
    category: "power-tools",
    price: 149,
    originalPrice: 189,
    images: [
      "https://readdy.ai/api/search-image?query=Makita%20orbital%20sander%20professional%20power%20tool%20teal%20on%20white%20background%2C%20product%20photography&width=800&height=800&seq=p021a&orientation=squarish",
      "https://readdy.ai/api/search-image?query=orbital%20sander%20pad%20close%20up%20professional%20finishing%20tool&width=800&height=800&seq=p021b&orientation=squarish",
      "https://readdy.ai/api/search-image?query=orbital%20sander%20sanding%20wood%20surface%20professional%20finishing%20work&width=800&height=800&seq=p021c&orientation=squarish",
      "https://readdy.ai/api/search-image?query=orbital%20sander%20dust%20collection%20bag%20professional%20tool%20feature&width=800&height=800&seq=p021d&orientation=squarish",
      "https://readdy.ai/api/search-image?query=orbital%20sander%20with%20battery%20professional%20cordless%20finishing%20tool&width=800&height=800&seq=p021e&orientation=squarish",
      "https://readdy.ai/api/search-image?query=orbital%20sander%20variable%20speed%20dial%20professional%20tool%20control&width=800&height=800&seq=p021f&orientation=squarish",
    ],
    description: "The Makita 18V Random Orbital Sander delivers smooth, swirl-free finishes on wood and other surfaces. The variable speed control (4,000-11,000 OPM) allows for precise finishing. The 5-inch hook-and-loop pad accepts standard sanding discs. The efficient dust collection system keeps the work area clean.",
    shortDesc: "18V cordless orbital sander with variable speed and efficient dust collection.",
    specs: {
      "Voltage": "18V LXT",
      "Pad Size": "5 inch",
      "Speed": "4,000-11,000 OPM",
      "Orbit Diameter": "3/32 inch",
      "Dust Collection": "Bag + Port",
      "Weight": "2.8 lbs",
      "Warranty": "3 Years",
    },
    rating: 4.7,
    reviewCount: 234,
    inStock: true,
    sku: "SL-SND-021",
    reviews: [
      { id: "r021a", author: "Diana Barnes", avatar: "DB", rating: 5, title: "Smooth finish every time", comment: "This sander produces incredibly smooth finishes. The variable speed is great for different materials.", date: "2024-11-03", verified: true },
      { id: "r021b", author: "Edward Ross", avatar: "ER", rating: 4, title: "Good cordless sander", comment: "Works well and the dust collection is effective. Battery life is good for finishing work.", date: "2024-10-10", verified: true },
      { id: "r021c", author: "Florence Ward", avatar: "FW", rating: 5, title: "Excellent for furniture", comment: "I use this for furniture refinishing and it gives beautiful results. The random orbital action prevents swirl marks.", date: "2024-09-18", verified: true },
    ],
  },
  {
    id: "p022",
    name: "Husky 46-inch Tool Cabinet",
    category: "storage",
    price: 699,
    originalPrice: 849,
    images: [
      "https://readdy.ai/api/search-image?query=Husky%20tool%20cabinet%20professional%20workshop%20storage%20steel%20on%20white%20background%2C%20product%20photography&width=800&height=800&seq=p022a&orientation=squarish",
      "https://readdy.ai/api/search-image?query=tool%20cabinet%20drawers%20open%20organized%20professional%20workshop%20storage&width=800&height=800&seq=p022b&orientation=squarish",
      "https://readdy.ai/api/search-image?query=tool%20cabinet%20lock%20security%20feature%20professional%20storage%20hardware&width=800&height=800&seq=p022c&orientation=squarish",
      "https://readdy.ai/api/search-image?query=tool%20cabinet%20full%20setup%20professional%20workshop%20organization&width=800&height=800&seq=p022d&orientation=squarish",
      "https://readdy.ai/api/search-image?query=tool%20cabinet%20casters%20wheels%20professional%20mobile%20storage&width=800&height=800&seq=p022e&orientation=squarish",
      "https://readdy.ai/api/search-image?query=tool%20cabinet%20top%20work%20surface%20professional%20workshop%20equipment&width=800&height=800&seq=p022f&orientation=squarish",
    ],
    description: "The Husky 46-inch Tool Cabinet features 9 drawers with 100 lb capacity each. The 18-gauge steel construction provides durability. The soft-close drawer slides prevent slamming. The keyed locking system secures all drawers simultaneously. Includes a non-slip liner in each drawer.",
    shortDesc: "46-inch 9-drawer steel tool cabinet with soft-close slides and keyed lock.",
    specs: {
      "Width": "46 inches",
      "Drawers": "9",
      "Drawer Capacity": "100 lbs each",
      "Steel Gauge": "18 Gauge",
      "Slides": "Soft-Close Ball Bearing",
      "Lock": "Keyed",
      "Warranty": "Lifetime",
    },
    rating: 4.6,
    reviewCount: 189,
    badge: "Premium",
    inStock: true,
    sku: "SL-CAB-022",
    reviews: [
      { id: "r022a", author: "George Patterson", avatar: "GP", rating: 5, title: "Great workshop cabinet", comment: "This cabinet has transformed my garage workshop. The drawers are smooth and the storage capacity is excellent.", date: "2024-11-23", verified: true },
      { id: "r022b", author: "Hannah Brooks", avatar: "HB", rating: 4, title: "Good quality cabinet", comment: "Solid steel construction and the soft-close drawers are a nice touch. Assembly took about 2 hours.", date: "2024-10-31", verified: true },
      { id: "r022c", author: "Ivan Jenkins", avatar: "IJ", rating: 5, title: "Worth the investment", comment: "I've had cheaper cabinets before and they don't compare. This one is built to last.", date: "2024-09-26", verified: true },
    ],
  },
  {
    id: "p023",
    name: "Bosch 12V Max Oscillating Tool",
    category: "power-tools",
    price: 199,
    originalPrice: 249,
    images: [
      "https://readdy.ai/api/search-image?query=Bosch%20oscillating%20multi-tool%20professional%20power%20tool%20blue%20on%20white%20background%2C%20product%20photography&width=800&height=800&seq=p023a&orientation=squarish",
      "https://readdy.ai/api/search-image?query=oscillating%20tool%20blade%20attachment%20professional%20multi-tool%20close%20up&width=800&height=800&seq=p023b&orientation=squarish",
      "https://readdy.ai/api/search-image?query=oscillating%20tool%20cutting%20grout%20tile%20professional%20contractor%20work&width=800&height=800&seq=p023c&orientation=squarish",
      "https://readdy.ai/api/search-image?query=oscillating%20tool%20accessories%20blades%20set%20professional%20hardware&width=800&height=800&seq=p023d&orientation=squarish",
      "https://readdy.ai/api/search-image?query=oscillating%20tool%20with%20battery%20professional%20cordless%20multi-tool&width=800&height=800&seq=p023e&orientation=squarish",
      "https://readdy.ai/api/search-image?query=oscillating%20tool%20variable%20speed%20control%20professional%20tool%20feature&width=800&height=800&seq=p023f&orientation=squarish",
    ],
    description: "The Bosch 12V Max Oscillating Tool is the most versatile tool in any toolkit. The Starlock accessory system provides maximum power transfer. Variable speed from 10,000-20,000 OPM for precise control. Compatible with hundreds of accessories for cutting, sanding, scraping, and more.",
    shortDesc: "12V oscillating multi-tool with Starlock system and variable speed control.",
    specs: {
      "Voltage": "12V MAX",
      "Speed": "10,000-20,000 OPM",
      "Oscillation Angle": "3.2°",
      "Accessory System": "Starlock",
      "Weight": "1.8 lbs",
      "Battery": "2x 2.0Ah",
      "Warranty": "3 Years",
    },
    rating: 4.8,
    reviewCount: 312,
    inStock: true,
    sku: "SL-OSC-023",
    reviews: [
      { id: "r023a", author: "Julia Coleman", avatar: "JC", rating: 5, title: "Most versatile tool I own", comment: "This oscillating tool can do so many things. I use it for cutting, sanding, and scraping on every project.", date: "2024-11-01", verified: true },
      { id: "r023b", author: "Kevin Perry", avatar: "KP", rating: 5, title: "Excellent multi-tool", comment: "The Starlock system makes accessory changes quick and the power transfer is noticeably better.", date: "2024-10-09", verified: true },
      { id: "r023c", author: "Laura Powell", avatar: "LP", rating: 4, title: "Great compact tool", comment: "Very useful for tight spaces and detail work. The variable speed gives good control.", date: "2024-09-23", verified: true },
    ],
  },
  {
    id: "p024",
    name: "3M Hard Hat Full Brim",
    category: "safety",
    price: 89,
    originalPrice: 109,
    images: [
      "https://readdy.ai/api/search-image?query=3M%20hard%20hat%20full%20brim%20professional%20safety%20equipment%20white%20on%20white%20background%2C%20product%20photography&width=800&height=800&seq=p024a&orientation=squarish",
      "https://readdy.ai/api/search-image?query=hard%20hat%20suspension%20system%20interior%20professional%20safety%20equipment&width=800&height=800&seq=p024b&orientation=squarish",
      "https://readdy.ai/api/search-image?query=hard%20hat%20in%20use%20construction%20site%20professional%20worker%20safety&width=800&height=800&seq=p024c&orientation=squarish",
      "https://readdy.ai/api/search-image?query=hard%20hat%20brim%20detail%20professional%20safety%20equipment%20close%20up&width=800&height=800&seq=p024d&orientation=squarish",
      "https://readdy.ai/api/search-image?query=hard%20hat%20adjustment%20ratchet%20professional%20safety%20equipment%20feature&width=800&height=800&seq=p024e&orientation=squarish",
      "https://readdy.ai/api/search-image?query=hard%20hat%20accessories%20slots%20professional%20safety%20equipment&width=800&height=800&seq=p024f&orientation=squarish",
    ],
    description: "The 3M Full Brim Hard Hat provides superior protection with ANSI/ISEA Z89.1 Type I Class E certification. The full brim design provides 360° sun and rain protection. The 4-point suspension system with ratchet adjustment ensures a comfortable, secure fit. Slots for accessories like earmuffs and face shields.",
    shortDesc: "Full brim hard hat with ANSI Type I Class E certification and 4-point suspension.",
    specs: {
      "Type": "Type I, Class E",
      "Certification": "ANSI/ISEA Z89.1",
      "Suspension": "4-Point Ratchet",
      "Brim": "Full Brim",
      "Material": "HDPE",
      "Accessory Slots": "Yes",
      "Warranty": "5 Years",
    },
    rating: 4.7,
    reviewCount: 256,
    inStock: true,
    sku: "SL-HAT-024",
    reviews: [
      { id: "r024a", author: "Martin Long", avatar: "ML", rating: 5, title: "Comfortable and protective", comment: "The full brim is great for outdoor work. The ratchet adjustment makes it easy to get a perfect fit.", date: "2024-11-15", verified: true },
      { id: "r024b", author: "Nancy Hughes", avatar: "NH", rating: 4, title: "Good hard hat", comment: "Comfortable for all-day wear and the full brim provides good sun protection. The accessory slots are useful.", date: "2024-10-23", verified: true },
      { id: "r024c", author: "Oliver Foster", avatar: "OF", rating: 5, title: "Essential safety gear", comment: "I wear this every day on the job site. It's comfortable, well-ventilated, and provides excellent protection.", date: "2024-09-10", verified: true },
    ],
  },
  {
    id: "p025",
    name: "Milwaukee 48-Piece Shockwave Bit Set",
    category: "fasteners",
    price: 79,
    originalPrice: 99,
    images: [
      "https://readdy.ai/api/search-image?query=Milwaukee%20Shockwave%20drill%20bit%20set%20professional%20hardware%20on%20white%20background%2C%20product%20photography&width=800&height=800&seq=p025a&orientation=squarish",
      "https://readdy.ai/api/search-image?query=drill%20bit%20set%20organized%20case%20professional%20hardware%20accessories&width=800&height=800&seq=p025b&orientation=squarish",
      "https://readdy.ai/api/search-image?query=drill%20bit%20close%20up%20tip%20professional%20hardware%20tool%20detail&width=800&height=800&seq=p025c&orientation=squarish",
      "https://readdy.ai/api/search-image?query=drill%20bit%20set%20in%20use%20drilling%20wood%20professional%20work&width=800&height=800&seq=p025d&orientation=squarish",
      "https://readdy.ai/api/search-image?query=impact%20driver%20bit%20set%20professional%20hardware%20accessories&width=800&height=800&seq=p025e&orientation=squarish",
      "https://readdy.ai/api/search-image?query=drill%20bit%20set%20storage%20case%20organized%20professional%20hardware%20kit&width=800&height=800&seq=p025f&orientation=squarish",
    ],
    description: "The Milwaukee 48-Piece Shockwave Impact Duty Bit Set is engineered for use with impact drivers. The Shockzone geometry absorbs peak torque and prevents breaking. The Wear Guard Tip provides 10x longer life. Includes drill bits, driver bits, and nut drivers in a compact case.",
    shortDesc: "48-piece impact duty bit set with Shockzone geometry and Wear Guard Tip.",
    specs: {
      "Pieces": "48",
      "Shank": "1/4\" Hex",
      "Material": "S2 Modified Steel",
      "Tip": "Wear Guard",
      "Compatibility": "Impact Drivers",
      "Case": "Compact Organizer",
      "Warranty": "Lifetime",
    },
    rating: 4.8,
    reviewCount: 423,
    badge: "Best Seller",
    inStock: true,
    sku: "SL-BIT-025",
    reviews: [
      { id: "r025a", author: "Patricia Coleman", avatar: "PC", rating: 5, title: "Excellent bit set", comment: "These bits last much longer than standard bits. The Shockzone design really does prevent breaking.", date: "2024-11-20", verified: true },
      { id: "r025b", author: "Quentin Barnes", avatar: "QB", rating: 5, title: "Great for impact drivers", comment: "Perfect companion for my impact driver. The bits are strong and the case keeps everything organized.", date: "2024-10-28", verified: true },
      { id: "r025c", author: "Rachel Ross", avatar: "RR", rating: 4, title: "Good quality bits", comment: "Durable bits that hold up to heavy use. The variety of sizes covers all my needs.", date: "2024-09-16", verified: true },
    ],
  },
  {
    id: "p026",
    name: "Dewalt 20V Impact Driver Kit",
    category: "power-tools",
    price: 229,
    originalPrice: 279,
    images: [
      "https://readdy.ai/api/search-image?query=DeWalt%20impact%20driver%20professional%20power%20tool%20yellow%20on%20white%20background%2C%20product%20photography&width=800&height=800&seq=p026a&orientation=squarish",
      "https://readdy.ai/api/search-image?query=impact%20driver%20hex%20chuck%20close%20up%20professional%20fastening%20tool&width=800&height=800&seq=p026b&orientation=squarish",
      "https://readdy.ai/api/search-image?query=impact%20driver%20driving%20screws%20professional%20contractor%20work&width=800&height=800&seq=p026c&orientation=squarish",
      "https://readdy.ai/api/search-image?query=impact%20driver%20LED%20light%20feature%20professional%20tool%20close%20up&width=800&height=800&seq=p026d&orientation=squarish",
      "https://readdy.ai/api/search-image?query=impact%20driver%20with%20battery%20kit%20professional%20cordless%20tool&width=800&height=800&seq=p026e&orientation=squarish",
      "https://readdy.ai/api/search-image?query=impact%20driver%20carrying%20bag%20professional%20tool%20kit%20storage&width=800&height=800&seq=p026f&orientation=squarish",
    ],
    description: "The DeWalt 20V MAX Impact Driver delivers 1,825 in-lbs of torque for the most demanding fastening applications. The 3-speed settings allow for precise control. The 1/4\" hex chuck accepts all standard impact bits. The compact design (5.55\" front to back) fits in tight spaces.",
    shortDesc: "20V impact driver with 1,825 in-lbs torque, 3-speed settings, and compact design.",
    specs: {
      "Voltage": "20V MAX",
      "Torque": "1,825 in-lbs",
      "Chuck": "1/4\" Hex",
      "Speed Settings": "3",
      "Speed": "0-2,800 RPM",
      "Weight": "2.8 lbs",
      "Warranty": "3 Years",
    },
    rating: 4.9,
    reviewCount: 356,
    badge: "Best Seller",
    inStock: true,
    sku: "SL-IMP-026",
    reviews: [
      { id: "r026a", author: "Samuel Ward", avatar: "SW", rating: 5, title: "Incredible torque", comment: "This impact driver drives screws effortlessly. The 3-speed settings give great control for different applications.", date: "2024-11-18", verified: true },
      { id: "r026b", author: "Tina Brooks", avatar: "TB", rating: 5, title: "Best impact driver", comment: "I've used many impact drivers and this is the best. The power is incredible and the compact size is great.", date: "2024-10-26", verified: true },
      { id: "r026c", author: "Ulysses Jenkins", avatar: "UJ", rating: 4, title: "Powerful and reliable", comment: "Great impact driver with plenty of power. The LED light is useful in dark areas.", date: "2024-09-14", verified: true },
    ],
  },
  {
    id: "p027",
    name: "Knipex 10-inch Cobra Pliers",
    category: "hand-tools",
    price: 95,
    originalPrice: 119,
    images: [
      "https://readdy.ai/api/search-image?query=Knipex%20Cobra%20pliers%20professional%20hand%20tool%20German%20made%20on%20white%20background%2C%20product%20photography&width=800&height=800&seq=p027a&orientation=squarish",
      "https://readdy.ai/api/search-image?query=Cobra%20pliers%20jaw%20adjustment%20professional%20hand%20tool%20close%20up&width=800&height=800&seq=p027b&orientation=squarish",
      "https://readdy.ai/api/search-image?query=pliers%20gripping%20pipe%20professional%20plumbing%20work%20contractor&width=800&height=800&seq=p027c&orientation=squarish",
      "https://readdy.ai/api/search-image?query=Cobra%20pliers%20handle%20grip%20professional%20ergonomic%20hand%20tool&width=800&height=800&seq=p027d&orientation=squarish",
      "https://readdy.ai/api/search-image?query=pliers%20jaw%20teeth%20detail%20professional%20gripping%20tool%20close%20up&width=800&height=800&seq=p027e&orientation=squarish",
      "https://readdy.ai/api/search-image?query=professional%20pliers%20set%20German%20quality%20hand%20tools&width=800&height=800&seq=p027f&orientation=squarish",
    ],
    description: "The Knipex 10-inch Cobra Pliers are the world's most popular water pump pliers. Made in Germany from chrome vanadium steel. The push-button adjustment allows for one-hand operation. The fine-tooth jaw pattern grips without slipping. The slim head design accesses tight spaces.",
    shortDesc: "10-inch German-made Cobra pliers with push-button adjustment and fine-tooth jaw.",
    specs: {
      "Length": "10 inches",
      "Capacity": "2-1/4 inch",
      "Material": "Chrome Vanadium Steel",
      "Adjustment": "Push-Button",
      "Made In": "Germany",
      "Jaw": "Fine-Tooth",
      "Warranty": "Lifetime",
    },
    rating: 4.9,
    reviewCount: 298,
    badge: "Premium",
    inStock: true,
    sku: "SL-PLR-027",
    reviews: [
      { id: "r027a", author: "Victoria Coleman", avatar: "VC", rating: 5, title: "Best pliers in the world", comment: "Knipex Cobra pliers are in a class of their own. The push-button adjustment is genius and the grip is incredible.", date: "2024-11-22", verified: true },
      { id: "r027b", author: "William Perry", avatar: "WP", rating: 5, title: "Worth the premium price", comment: "More expensive than other pliers but the quality is unmatched. These will last a lifetime.", date: "2024-10-30", verified: true },
      { id: "r027c", author: "Xena Powell", avatar: "XP", rating: 5, title: "German engineering at its best", comment: "The precision and quality of these pliers is remarkable. The one-hand adjustment is very convenient.", date: "2024-09-27", verified: true },
    ],
  },
  {
    id: "p028",
    name: "Leatherman Wave+ Multi-Tool",
    category: "hand-tools",
    price: 119,
    originalPrice: 149,
    images: [
      "https://readdy.ai/api/search-image?query=Leatherman%20Wave%20multi-tool%20professional%20stainless%20steel%20on%20white%20background%2C%20product%20photography&width=800&height=800&seq=p028a&orientation=squarish",
      "https://readdy.ai/api/search-image?query=multi-tool%20open%20all%20tools%20deployed%20professional%20hardware&width=800&height=800&seq=p028b&orientation=squarish",
      "https://readdy.ai/api/search-image?query=multi-tool%20pliers%20close%20up%20professional%20stainless%20steel%20tool&width=800&height=800&seq=p028c&orientation=squarish",
      "https://readdy.ai/api/search-image?query=multi-tool%20in%20use%20professional%20contractor%20work%20versatile%20tool&width=800&height=800&seq=p028d&orientation=squarish",
      "https://readdy.ai/api/search-image?query=multi-tool%20leather%20sheath%20carrying%20case%20professional%20hardware&width=800&height=800&seq=p028e&orientation=squarish",
      "https://readdy.ai/api/search-image?query=multi-tool%20blade%20knife%20professional%20stainless%20steel%20close%20up&width=800&height=800&seq=p028f&orientation=squarish",
    ],
    description: "The Leatherman Wave+ Multi-Tool is the most popular multi-tool in the world. Features 18 tools including needlenose pliers, wire cutters, knives, saws, files, and screwdrivers. All tools can be accessed from the outside without opening the handles. Made from 420HC stainless steel.",
    shortDesc: "18-tool multi-tool with outside-accessible tools and 420HC stainless steel construction.",
    specs: {
      "Tools": "18",
      "Material": "420HC Stainless Steel",
      "Weight": "8.5 oz",
      "Length Closed": "4 inches",
      "Length Open": "6.4 inches",
      "Sheath": "Leather",
      "Warranty": "25 Years",
    },
    rating: 4.8,
    reviewCount: 512,
    badge: "Best Seller",
    inStock: true,
    sku: "SL-MLT-028",
    reviews: [
      { id: "r028a", author: "Yolanda Long", avatar: "YL", rating: 5, title: "Best multi-tool ever", comment: "I've carried a Leatherman Wave for 10 years. It's been on every job site and never let me down.", date: "2024-11-16", verified: true },
      { id: "r028b", author: "Zachary Hughes", avatar: "ZH", rating: 5, title: "Essential everyday carry", comment: "The outside-accessible tools are a game changer. I use this tool every single day.", date: "2024-10-24", verified: true },
      { id: "r028c", author: "Alice Foster", avatar: "AF", rating: 4, title: "Great multi-tool", comment: "Excellent quality and the 25-year warranty shows Leatherman stands behind their product.", date: "2024-09-12", verified: true },
    ],
  },
  {
    id: "p029",
    name: "Bosch 18V Compact Router",
    category: "power-tools",
    price: 289,
    originalPrice: 349,
    images: [
      "https://readdy.ai/api/search-image?query=Bosch%20compact%20router%20professional%20woodworking%20power%20tool%20blue%20on%20white%20background%2C%20product%20photography&width=800&height=800&seq=p029a&orientation=squarish",
      "https://readdy.ai/api/search-image?query=router%20bit%20collet%20close%20up%20professional%20woodworking%20tool&width=800&height=800&seq=p029b&orientation=squarish",
      "https://readdy.ai/api/search-image?query=router%20routing%20wood%20edge%20professional%20carpenter%20work&width=800&height=800&seq=p029c&orientation=squarish",
      "https://readdy.ai/api/search-image?query=compact%20router%20base%20adjustment%20professional%20woodworking%20tool&width=800&height=800&seq=p029d&orientation=squarish",
      "https://readdy.ai/api/search-image?query=router%20with%20battery%20professional%20cordless%20woodworking%20tool&width=800&height=800&seq=p029e&orientation=squarish",
      "https://readdy.ai/api/search-image?query=router%20accessories%20bits%20set%20professional%20woodworking%20hardware&width=800&height=800&seq=p029f&orientation=squarish",
    ],
    description: "The Bosch 18V Compact Router delivers professional routing performance without the cord. The 1/4\" collet accepts standard router bits. The micro-fine depth adjustment allows for precise depth settings. The soft-start feature prevents bit grab. Compatible with all Bosch 18V batteries.",
    shortDesc: "18V cordless compact router with micro-fine depth adjustment and soft-start.",
    specs: {
      "Voltage": "18V",
      "Collet": "1/4 inch",
      "Speed": "16,000-35,000 RPM",
      "Depth Adjustment": "Micro-Fine",
      "Soft-Start": "Yes",
      "Weight": "3.3 lbs",
      "Warranty": "3 Years",
    },
    rating: 4.7,
    reviewCount: 187,
    inStock: true,
    sku: "SL-RTR-029",
    reviews: [
      { id: "r029a", author: "Benjamin Barnes", avatar: "BB", rating: 5, title: "Excellent compact router", comment: "The cordless freedom is amazing for routing. The micro-fine depth adjustment is very precise.", date: "2024-11-10", verified: true },
      { id: "r029b", author: "Catherine Ross", avatar: "CR", rating: 4, title: "Good cordless router", comment: "Works well for edge routing and template work. The soft-start is a nice safety feature.", date: "2024-10-18", verified: true },
      { id: "r029c", author: "Douglas Ward", avatar: "DW", rating: 5, title: "Great for woodworking", comment: "I use this for all my routing work now. The cordless design makes it so much more convenient.", date: "2024-09-26", verified: true },
    ],
  },
  {
    id: "p030",
    name: "Ridgid 18V Wet/Dry Vacuum",
    category: "storage",
    price: 179,
    originalPrice: 219,
    images: [
      "https://readdy.ai/api/search-image?query=Ridgid%20wet%20dry%20vacuum%20professional%20shop%20vac%20orange%20on%20white%20background%2C%20product%20photography&width=800&height=800&seq=p030a&orientation=squarish",
      "https://readdy.ai/api/search-image?query=shop%20vacuum%20hose%20attachment%20professional%20cleaning%20tool&width=800&height=800&seq=p030b&orientation=squarish",
      "https://readdy.ai/api/search-image?query=wet%20dry%20vacuum%20in%20use%20cleaning%20workshop%20professional%20work&width=800&height=800&seq=p030c&orientation=squarish",
      "https://readdy.ai/api/search-image?query=shop%20vacuum%20filter%20professional%20cleaning%20equipment%20close%20up&width=800&height=800&seq=p030d&orientation=squarish",
      "https://readdy.ai/api/search-image?query=wet%20dry%20vacuum%20with%20battery%20professional%20cordless%20cleaning%20tool&width=800&height=800&seq=p030e&orientation=squarish",
      "https://readdy.ai/api/search-image?query=shop%20vacuum%20accessories%20attachments%20professional%20cleaning%20kit&width=800&height=800&seq=p030f&orientation=squarish",
    ],
    description: "The Ridgid 18V Wet/Dry Vacuum provides powerful cordless cleaning for the job site. The 6-gallon capacity handles large debris and liquid spills. The auto-cleaning filter technology maintains suction power. Compatible with all Ridgid 18V batteries. Includes multiple attachments for versatile cleaning.",
    shortDesc: "18V cordless 6-gallon wet/dry vacuum with auto-cleaning filter technology.",
    specs: {
      "Voltage": "18V",
      "Capacity": "6 Gallons",
      "Hose Diameter": "1-7/8 inch",
      "Filter": "Auto-Cleaning",
      "Wet/Dry": "Both",
      "Weight": "9.5 lbs",
      "Warranty": "Lifetime",
    },
    rating: 4.6,
    reviewCount: 234,
    inStock: true,
    sku: "SL-VAC-030",
    reviews: [
      { id: "r030a", author: "Eleanor Coleman", avatar: "EC", rating: 5, title: "Great cordless vacuum", comment: "The cordless design is perfect for the job site. The 6-gallon capacity is more than enough for most cleanups.", date: "2024-11-14", verified: true },
      { id: "r030b", author: "Franklin Perry", avatar: "FP", rating: 4, title: "Good shop vacuum", comment: "Works well for both wet and dry pickup. The auto-cleaning filter is a great feature.", date: "2024-10-22", verified: true },
      { id: "r030c", author: "Grace Powell", avatar: "GP", rating: 5, title: "Essential job site tool", comment: "I use this vacuum on every job. The cordless freedom and powerful suction make cleanup quick and easy.", date: "2024-09-20", verified: true },
    ],
  },
];
